import {
  Divider,
  Button,
  Popconfirm,
  Typography,
  message,
  Tooltip,
  Tag,
  Row,
  Col,
  Tabs,
} from 'antd'
import {
  ExclamationCircleOutlined,
  HighlightOutlined,
  StarOutlined,
  DeleteOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import PropTypes from 'prop-types'

import useQueryParams from '../../hooks/useQueryParams'
import api from '../../api'
import { useAsync } from '../../hooks'
import { toPath } from '../../routes'
import Comments from '../Comments'
import { useCanSign, useCurrentUser } from '../../redux/slices/session'
import { ListError, PageHeader } from '../Common'
import ActionButtons from '../Common/ActionButtons'
import NavigationButtons from '../Common/NavigationButtons'
import { formatDOB } from '../../lib/datetime'
import PDFPreview from '../Common/PDFPreview'
import Loading from '../Loading'
import DownloadButtons from '../Common/DownloadButtons'
import { useReports } from '../../redux/slices/reports'
import { reportShowPath } from '../../lib/reports'
import TransmissionSidebar from '../Common/TransmissionSidebar'

dayjs.extend(utc)

const { Text } = Typography
const { TabPane } = Tabs

export default function ShowReport({ isV2Report = false }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const [query, , getPathWithQuery] = useQueryParams({})

  const getReport = useCallback(
    () => api.reports.get(id, query, isV2Report),
    [id, query, isV2Report],
  )
  const {
    error,
    pending,
    value: report,
    execute: reloadReport,
  } = useAsync(getReport)

  const { role } = useCurrentUser()
  const isClinician = role === 'clinician'
  const canSign = useCanSign()

  const [loading, setLoading] = useState(null)

  const [isPdfPending, setIsPdfPending] = useState(() => {
    try {
      const stored = localStorage.getItem('activePdfBatch')
      if (!stored) return false
      const { reportIds } = JSON.parse(stored)
      return Array.isArray(reportIds) && reportIds.includes(id)
    } catch {
      return false
    }
  })

  useEffect(() => {
    const stored = localStorage.getItem('activePdfBatch')
    if (!stored) return undefined
    let cancelled = false
    try {
      const { batchId, reportIds } = JSON.parse(stored)
      if (!Array.isArray(reportIds) || !reportIds.includes(id)) return undefined
      api.reports.getBatchSignatureStatus(batchId).then(data => {
        if (cancelled) return
        if (data.finishedAt || data.error) {
          localStorage.removeItem('activePdfBatch')
          setIsPdfPending(false)
        }
      }).catch(() => {})
    } catch {
      // ignore malformed storage
    }
    return () => { cancelled = true }
  }, [id])

  const reports = useReports(report?.cycleId)

  /* Helpers */
  const reportPath = useCallback(
    record =>
      record && {
        pathname: reportShowPath(record),
        search: new URLSearchParams(query).toString(),
      },
    [query],
  )

  const findPaths = () => {
    const currentIndex = reports.findIndex(r => r.id === id)

    if (reports?.length <= 1)
      return {
        next: null,
        prev: null,
      }

    if (currentIndex === 0) {
      return {
        next: reportPath(reports[currentIndex + 1]),
        prev: null,
      }
    }
    if (currentIndex === reports.length - 1) {
      return {
        next: null,
        prev: reportPath(reports[currentIndex - 1]),
      }
    }
    return {
      next: reportPath(reports[currentIndex + 1]),
      prev: reportPath(reports[currentIndex - 1]),
    }
  }

  /* Actions */
  const handleDelete = useCallback(async () => {
    try {
      if (isV2Report) {
        await api.remoteChecks.destroy(id)
      } else {
        await api.reports.destroy(id)
      }
      message.success('Report successfully removed')
      navigate(-1)
    } catch (e) {
      message.error(`There was an error removing this report: ${e.message}`)
    }
  }, [navigate, id, isV2Report])

  const handleSetMain = useCallback(
    async transmissionId => {
      if (!isV2Report || !report) return
      setLoading('main')
      try {
        await api.remoteChecks.addTransmission(report.id, transmissionId, true)
        await reloadReport()
      } finally {
        setLoading(null)
      }
    },
    [isV2Report, reloadReport, report],
  )

  const handleRemove = useCallback(
    async transmissionId => {
      if (!isV2Report || !report) return
      setLoading('remove')
      try {
        await api.remoteChecks.removeTransmission(report.id, transmissionId)
        await reloadReport()
      } finally {
        setLoading(null)
      }
    },
    [isV2Report, reloadReport, report],
  )

  /* Signing */
  const { pending: signing, execute: handleSign } = useAsync(async () => {
    try {
      await api.reports.signReport(id, isV2Report)
      await reloadReport()
      message.success('The report has been signed')
    } catch (e) {
      if (e.response?.errors?.bbReport === 'not_present') {
        message.error(
          'There was an error signing this report: Please fill out the BioBridge report before signing.',
        )
      } else {
        message.error(`There was an error signing this report: ${e.message}`)
      }
    }
  }, false)

  /* Loading / Error states */
  if (pending) {
    return <Loading large />
  }

  if (error) {
    return (
      <ListError
        message="There was an error loading this report"
        description={error.message}
      />
    )
  }

  /* Destructure report */
  const { patient } = report
  const transmissionsRaw = report?.transmissions || []
  const transmissions = [...transmissionsRaw].sort((a, b) => {
    const aMain = a.main ? 1 : 0
    const bMain = b.main ? 1 : 0
    return bMain - aMain // main=true first
  })

  /* Permissions */
  const canEdit = () => {
    if (!isClinician) return true

    const now = dayjs.utc()
    const receivedAt = dayjs.utc(report.receivedAt)

    // Only allowed for one hour after the report edition
    const diffSeconds = now.diff(receivedAt, 'second')
    return diffSeconds <= 3600
  }

  /* Render */
  return (
    <div>
      <NavigationButtons
        nextPath={findPaths().next}
        prevPath={findPaths().prev}
      />

      <PageHeader
        title={`${patient.firstName} ${patient.lastName}`}
        subTitle={`MRN: ${patient.practiceMrn || 'N/A'} — DOB: ${formatDOB(
          patient.birthDate,
        )}`}
        onBack={!isClinician && (() => navigate(-1))}
        extra={
          <>
            {isPdfPending && (
              <Tag icon={<SyncOutlined spin />} color="processing">
                Signed reports will be available soon
              </Tag>
            )}
            {report.critical && (
              <>
                <ExclamationCircleOutlined style={{ color: 'red' }} />
                <Text type="danger">Requires Attention</Text>
              </>
            )}

            {!isClinician && (
              <Link
                key="edit-patient"
                to={toPath.patients.patient.edit({ id: patient.id })}
              >
                <Button>Edit Patient</Button>
              </Link>
            )}

            {canSign &&
              (!report.biobridgeReport ? (
                <Tooltip
                  placement="left"
                  title="Add a report to be able to sign it"
                >
                  <Button
                    type="dashed"
                    icon={<HighlightOutlined />}
                    loading={signing}
                    disabled
                  >
                    Sign
                  </Button>
                </Tooltip>
              ) : (
                <Popconfirm
                  title="Are you sure you want to sign this report?"
                  onConfirm={handleSign}
                  okText="Yes"
                  cancelText="No"
                  placement="topRight"
                >
                  <Button
                    type="dashed"
                    icon={<HighlightOutlined />}
                    loading={signing}
                  >
                    Sign
                  </Button>
                </Popconfirm>
              ))}

            {canEdit() && (
              <>
                {report.isArchived ? (
                  <Button type="dashed" disabled>
                    Archived
                  </Button>
                ) : (
                  <Link
                    key="edit"
                    to={
                      isV2Report
                        ? getPathWithQuery(
                            toPath.remoteChecks.remoteCheck.edit({ id }),
                            query,
                          )
                        : getPathWithQuery(
                            toPath.reports.report.edit({ id }),
                            query,
                          )
                    }
                  >
                    <Button>Edit Report</Button>
                  </Link>
                )}
                {!isClinician && (
                  <DownloadButtons resource={report} pdfPending={isPdfPending} />
                )}
              </>
            )}
          </>
        }
      />

      {/* Removed redundant PatientSummary component to save vertical space */}

      <Divider />

      <Row>
        <Col span={isClinician ? 20 : 16}>
          <Tabs className="tabs">
            {report?.biobridgeReport && (
              <TabPane tab="Report" key="report">
                <PDFPreview path={report.biobridgeReport} showAllPages />
              </TabPane>
            )}

            {transmissions.length > 0
              ? transmissions.map(t => (
                  <TabPane
                    tab={
                      <span>
                        {dayjs(t.createdAt).format('MMM Do YYYY')}{' '}
                        {t.main && (
                          <StarOutlined
                            style={{ color: 'green', marginLeft: 4 }}
                          />
                        )}
                      </span>
                    }
                    key={`transmission-${t.id}`}
                  >
                    {!isClinician && isV2Report && (
                      <div style={{ textAlign: 'right', marginBottom: 8 }}>
                        {t.main ? (
                          <StarOutlined
                            style={{
                              color: 'green',
                              fontSize: 16,
                              marginRight: 8,
                            }}
                          />
                        ) : (
                          <Button
                            size="medium"
                            type="link"
                            icon={<StarOutlined />}
                            onClick={() => handleSetMain(t.id)}
                            loading={loading === 'main'}
                            style={{ marginRight: 8 }}
                          >
                            Set as main
                          </Button>
                        )}
                        <Button
                          size="small"
                          type="link"
                          icon={<DeleteOutlined />}
                          style={{ marginRight: 8 }}
                          onClick={() => handleRemove(t.id)}
                          loading={loading === 'remove'}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                    <PDFPreview
                      path={t.fileUrl || t.file_url || '/sample_report.pdf'}
                      showAllPages
                    />
                  </TabPane>
                ))
              : report.deviceReport && (
                  <TabPane tab="Transmission" key="transmission">
                    <PDFPreview path={report.deviceReport} showAllPages />
                  </TabPane>
                )}
          </Tabs>
        </Col>

        {!isClinician && (
          <Col span={8}>
            <TransmissionSidebar
              patient={patient}
              practiceId={report.practice}
              remoteCheckId={isV2Report ? report.id : null}
              includedTransmissions={report.transmissions?.map(t => t.id) || []}
              mainTransmissionId={report.transmissions?.find(t => t.main)?.id}
              onUpdated={reloadReport}
            />
          </Col>
        )}
      </Row>

      <ActionButtons onDelete={handleDelete} />

      {!isClinician && (
        <Comments
          collectionName={isV2Report ? 'remote-checks' : 'reports'}
          id={report.id}
        />
      )}

      <style>{`
        .tabs {
          margin-right: 20px;
          max-height: 88vh; /* increased to utilise space after header removal */
          overflow-y: auto;
        }
      `}</style>
    </div>
  )
}

ShowReport.propTypes = {
  isV2Report: PropTypes.bool,
}
